import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit3, FiPackage, FiTrendingUp, FiTrendingDown, FiRefreshCw, FiSave, FiX } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { productosService, inventarioService } from '../../services';

const ProductoInventario = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [producto, setProducto] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estadisticas, setEstadisticas] = useState({
    total_entradas: 0,
    total_salidas: 0,
    stock_actual: 0
  });

  // Estados para edición inline
  const [isEditingStock, setIsEditingStock] = useState(false);
  const [editedMinStock, setEditedMinStock] = useState('');
  const [editedMaxStock, setEditedMaxStock] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadProductoYMovimientos();
  }, [id]);

  const loadProductoYMovimientos = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Cargar producto
      const resultProducto = await productosService.getProducto(id);
      if (resultProducto.success) {
        setProducto(resultProducto.data);

        // Cargar movimientos de inventario
        const resultMovimientos = await inventarioService.getMovementsByProduct(id, {
          skip: 0,
          limit: 50
        });

        if (resultMovimientos.success) {
          const movs = resultMovimientos.data || [];
          setMovimientos(movs);

          // Calcular estadísticas
          const entradas = movs.filter(m =>
            ['purchase', 'adjustment'].includes(m.movement_type) && m.quantity > 0
          ).reduce((sum, m) => sum + Math.abs(m.quantity), 0);

          const salidas = movs.filter(m =>
            ['sale', 'adjustment'].includes(m.movement_type) && m.quantity < 0
          ).reduce((sum, m) => sum + Math.abs(m.quantity), 0);

          setEstadisticas({
            total_entradas: entradas,
            total_salidas: salidas,
            stock_actual: resultProducto.data.current_stock || 0
          });
        }
      } else {
        setError('No se pudo cargar el producto');
      }
    } catch (error) {
      console.error('Error cargando inventario:', error);
      setError('Error de conexión al cargar la información');
    } finally {
      setIsLoading(false);
    }
  };

  const getMovementTypeLabel = (type) => {
    const types = {
      'purchase': 'Compra',
      'sale': 'Venta',
      'adjustment': 'Ajuste',
      'transfer': 'Transferencia',
      'production': 'Producción',
      'return': 'Devolución'
    };
    return types[type] || type;
  };

  const getMovementTypeColor = (type, quantity) => {
    if (quantity > 0) return 'success';
    if (quantity < 0) return 'danger';
    return 'secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditStock = () => {
    setEditedMinStock(producto.min_stock || 0);
    setEditedMaxStock(producto.max_stock || 0);
    setIsEditingStock(true);
  };

  const handleCancelEdit = () => {
    setIsEditingStock(false);
    setEditedMinStock('');
    setEditedMaxStock('');
  };

  const handleSaveStock = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const updateData = {
        min_stock: parseFloat(editedMinStock) || 0,
        max_stock: parseFloat(editedMaxStock) || 0
      };

      const result = await productosService.updateProducto(id, updateData);

      if (result.success) {
        setProducto(result.data);
        setIsEditingStock(false);
        setEditedMinStock('');
        setEditedMaxStock('');
        // Mensaje de éxito
        alert('Stock mínimo y máximo actualizados correctamente');
      } else {
        setError(result.message || 'Error al actualizar stock');
      }
    } catch (error) {
      console.error('Error al guardar stock:', error);
      setError('Error al guardar los cambios');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando movimientos de inventario..." />;
  }

  if (!producto) {
    return (
      <div className="error-container">
        <h2>Producto no encontrado</h2>
        <button onClick={() => navigate('/system/productos')} className="btn btn--primary">
          Volver a Productos
        </button>
      </div>
    );
  }

  const productTabs = [
    { id: 'datos', label: 'Datos Producto', route: '' },
    { id: 'ventas', label: 'Ventas', route: '/ventas' },
    { id: 'inventario', label: 'Inventario', route: '/inventario' },
    { id: 'formulacion', label: 'Formulación', route: '/formulacion' }
  ];

  return (
    <div className="cliente-detalle-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/productos')}
          type="button"
        >
          <FiArrowLeft /> Volver a Productos
        </button>

        <h1 className="form-title">
          INVENTARIO - {producto.name}
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--secondary"
            onClick={loadProductoYMovimientos}
            type="button"
          >
            <FiRefreshCw /> Actualizar
          </button>
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/system/productos/${id}/editar`)}
            type="button"
          >
            <FiEdit3 /> Editar Producto
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="cliente-detail-form">
        {error && (
          <div className="alert alert--danger">
            {error}
          </div>
        )}

        <div className="form-columns">
          {/* Columna Izquierda - Resumen */}
          <div className="form-column">
            {/* Tabla de Stock Actual - EDITABLE */}
            <div className="form-section">
              <div className="section-header">
                <FiPackage className="section-icon" />
                <h2 className="section-title">Stock Actual</h2>
                {!isEditingStock && (
                  <button
                    className="btn btn--secondary"
                    onClick={handleEditStock}
                    style={{ marginLeft: 'auto', fontSize: '12px', padding: '6px 12px' }}
                  >
                    <FiEdit3 /> Editar
                  </button>
                )}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginTop: '16px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #4a3470' }}>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Stock Actual
                      </th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Stock Mínimo
                      </th>
                      <th style={{
                        padding: '12px',
                        textAlign: 'left',
                        fontSize: '11px',
                        fontWeight: '700',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Stock Máximo
                      </th>
                      {isEditingStock && (
                        <th style={{
                          padding: '12px',
                          textAlign: 'center',
                          fontSize: '11px',
                          fontWeight: '700',
                          color: '#6b7280',
                          textTransform: 'uppercase'
                        }}>
                          Acciones
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{
                        padding: '14px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#667eea'
                      }}>
                        {estadisticas.stock_actual} {producto.unit || 'unidades'}
                      </td>
                      <td style={{ padding: '14px', fontSize: '14px', color: '#374151' }}>
                        {isEditingStock ? (
                          <input
                            type="number"
                            value={editedMinStock}
                            onChange={(e) => setEditedMinStock(e.target.value)}
                            min="0"
                            step="0.01"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                            placeholder="Stock mínimo"
                          />
                        ) : (
                          <span>{producto.min_stock || 0} {producto.unit || 'unidades'}</span>
                        )}
                      </td>
                      <td style={{ padding: '14px', fontSize: '14px', color: '#374151' }}>
                        {isEditingStock ? (
                          <input
                            type="number"
                            value={editedMaxStock}
                            onChange={(e) => setEditedMaxStock(e.target.value)}
                            min="0"
                            step="0.01"
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                            placeholder="Stock máximo"
                          />
                        ) : (
                          <span>{producto.max_stock || 0} {producto.unit || 'unidades'}</span>
                        )}
                      </td>
                      {isEditingStock && (
                        <td style={{ padding: '14px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              className="btn btn--success"
                              onClick={handleSaveStock}
                              disabled={isSaving}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              <FiSave /> {isSaving ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button
                              className="btn btn--ghost"
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              <FiX /> Cancelar
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Estadísticas */}
            <div className="form-section">
              <div className="section-header">
                <h2 className="section-title">Estadísticas de Movimientos</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Total Entradas</label>
                <div className="form-value" style={{ color: '#10b981', fontWeight: '600' }}>
                  <FiTrendingUp style={{ marginRight: '8px' }} />
                  {estadisticas.total_entradas} {producto.unit || 'unidades'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Total Salidas</label>
                <div className="form-value" style={{ color: '#ef4444', fontWeight: '600' }}>
                  <FiTrendingDown style={{ marginRight: '8px' }} />
                  {estadisticas.total_salidas} {producto.unit || 'unidades'}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Total de Movimientos</label>
                <div className="form-value">
                  {movimientos.length}
                </div>
              </div>
            </div>

            {/* Información del Producto */}
            <div className="form-section">
              <div className="section-header">
                <h2 className="section-title">Información del Producto</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Código</label>
                <div className="form-value">{producto.code || 'N/A'}</div>
              </div>

              {producto.barcode && (
                <div className="form-group">
                  <label className="form-label">Código de Barras</label>
                  <div className="form-value">{producto.barcode}</div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Seguimiento de Inventario</label>
                <div className="form-value">
                  {producto.track_inventory ? 'SÍ' : 'NO'}
                </div>
              </div>

              {producto.batch_control && (
                <div className="form-group">
                  <label className="form-label">Control por Lote</label>
                  <div className="form-value">SÍ</div>
                </div>
              )}
            </div>
          </div>

          {/* Columna Derecha - Movimientos */}
          <div className="form-column">
            <div className="form-section">
              <div className="section-header">
                <h2 className="section-title">Historial de Movimientos</h2>
              </div>

              {movimientos.length === 0 ? (
                <div className="empty-state">
                  <p>No hay movimientos de inventario registrados para este producto</p>
                </div>
              ) : (
                <div className="movements-list" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {movimientos.map((mov) => (
                    <div key={mov.id} className="movement-card" style={{
                      padding: '16px',
                      marginBottom: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <span className={`status-badge status-badge--${getMovementTypeColor(mov.movement_type, mov.quantity)}`}>
                            {getMovementTypeLabel(mov.movement_type)}
                          </span>
                          <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
                            {formatDate(mov.created_at)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: mov.quantity > 0 ? '#10b981' : '#ef4444'
                          }}>
                            {mov.quantity > 0 ? '+' : ''}{mov.quantity} {mov.product?.unit || producto.unit || ''}
                          </div>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px',
                        fontSize: '0.9em',
                        color: '#4b5563',
                        marginTop: '12px'
                      }}>
                        <div>
                          <strong>Stock Anterior:</strong> {mov.stock_before || 0}
                        </div>
                        <div>
                          <strong>Stock Nuevo:</strong> {mov.stock_after || 0}
                        </div>
                        {mov.unit_cost && (
                          <>
                            <div>
                              <strong>Costo Unit.:</strong> ${typeof mov.unit_cost === 'number' ? mov.unit_cost.toFixed(2) : '0.00'}
                            </div>
                            <div>
                              <strong>Valor Total:</strong> ${typeof mov.unit_cost === 'number' ? (Math.abs(mov.quantity) * mov.unit_cost).toFixed(2) : '0.00'}
                            </div>
                          </>
                        )}
                      </div>

                      {mov.reason && (
                        <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '4px', fontSize: '0.9em' }}>
                          <strong>Razón:</strong> {mov.reason}
                        </div>
                      )}

                      {mov.notes && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#6b7280' }}>
                          <strong>Notas:</strong> {mov.notes}
                        </div>
                      )}

                      {mov.batch_number && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#6b7280' }}>
                          <strong>Lote:</strong> {mov.batch_number}
                        </div>
                      )}

                      {mov.user?.full_name && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#9ca3af' }}>
                          Por: {mov.user.full_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/productos/${id}`}
        tabs={productTabs}
        currentTab="inventario"
      />
    </div>
  );
};

export default ProductoInventario;
